import { watch } from "vue";
import type { userProfil } from "~/utils/interface/user";

type FbqFn = (...args: unknown[]) => void;

type AdvancedMatchingPayload = {
  em?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  ct?: string;
  country?: string;
  external_id?: string;
};

const normalizeEmail = (value: unknown): string | undefined => {
  const email = String(value ?? "").trim().toLowerCase();
  return email.includes("@") ? email : undefined;
};

const normalizePhone = (value: unknown): string | undefined => {
  const digits = String(value ?? "").replace(/\D+/g, "");
  if (!digits) return undefined;

  const normalized = digits.startsWith("00") ? digits.slice(2) : digits;

  if (normalized.startsWith("216")) return normalized;
  if (normalized.length === 8) return `216${normalized}`;

  return normalized;
};

const normalizeText = (value: unknown): string | undefined => {
  const text = String(value ?? "").trim().toLowerCase();
  return text.length ? text : undefined;
};

const splitFullName = (fullName: unknown): { fn?: string; ln?: string } => {
  const normalized = normalizeText(fullName);
  if (!normalized) return {};

  const parts = normalized.split(/\s+/).filter(Boolean);
  if (!parts.length) return {};

  return {
    fn: parts[0],
    ln: parts.slice(1).join(" ") || undefined,
  };
};

const buildAdvancedMatchingPayload = (
  profile?: Partial<userProfil>
): AdvancedMatchingPayload => {
  if (!profile || !Object.keys(profile).length) return {};

  const nameParts = splitFullName(profile.fullName);
  const payload: AdvancedMatchingPayload = {
    em: normalizeEmail(profile.email),
    ph: normalizePhone(profile.phone),
    fn: nameParts.fn,
    ln: nameParts.ln,
    ct: normalizeText(profile.city),
    country: "tn",
    external_id:
      typeof profile.id === "number" && Number.isFinite(profile.id)
        ? String(profile.id)
        : undefined,
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => typeof value !== "undefined")
  ) as AdvancedMatchingPayload;
};

export default defineNuxtPlugin(() => {
  const dashStore = useDashboardStore();
  const runtimeConfig = useRuntimeConfig();

  const pixels = runtimeConfig.public.metapixel as
    | Record<string, { id?: string | number }>
    | undefined;
  const pixelId = String(pixels?.default?.id ?? "").trim();
  if (!pixelId) return;

  let lastSentSignature = "";

  const tryInitAdvancedMatching = () => {
    const fbq = window.fbq as FbqFn | undefined;
    if (typeof fbq !== "function") return false;

    const payload = buildAdvancedMatchingPayload(dashStore.getUserProfil);
    if (!Object.keys(payload).length) return false;

    const signature = `${pixelId}:${JSON.stringify(payload)}`;
    if (signature === lastSentSignature) return true;

    fbq("init", pixelId, payload);
    lastSentSignature = signature;
    return true;
  };

  watch(
    () => ({
      id: dashStore.getUserProfil?.id,
      email: dashStore.getUserProfil?.email,
      phone: dashStore.getUserProfil?.phone,
      fullName: dashStore.getUserProfil?.fullName,
      city: dashStore.getUserProfil?.city,
    }),
    () => {
      tryInitAdvancedMatching();
    },
    { immediate: true }
  );

  let attempts = 0;
  const maxAttempts = 120;
  const timer = window.setInterval(() => {
    attempts += 1;

    if (tryInitAdvancedMatching() || attempts >= maxAttempts) {
      window.clearInterval(timer);
    }
  }, 1000);
});
